// ─────────────────────────────────────────────────────────────────────────────
// GateTab.tsx — Gate 1 · Writer → Director, the last tab (Writer.dc.html
// :204-305). The queue (oldest first), the selected script as a plain document
// with Sawyer's own checks, the note for Marty (no length limit), Pass / Kick
// back, and the gate chat. Every press goes to the backend and the tab renders
// what the backend then holds — never an optimistic echo.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import type { Board, QueueRow } from './sawyerApi';
import { gateChat, gateDecide } from './sawyerApi';
import { A, G, R, docLines, runtime, since, tagOfState, scriptForGate } from './compose';
import { DocView, bubble, sx } from './Cards';

export interface GateTabProps {
  board: Board;
  sel: string | null;
  setSel: (eid: string) => void;
  onBoard: (b: Board | null) => void;
}

export default function GateTab({ board, sel, setSel, onBoard }: GateTabProps) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queue = board.gate.queue;
  const q: QueueRow | undefined = queue.find((x) => x.episode_id === sel) || board.gate.waiting[0] || queue[0];
  const waiting = board.gate.waiting.length;
  const script = q ? scriptForGate(board, q.episode_id) : undefined;
  const version = q?.version ?? 0;
  const chat = q ? board.gate.chats.find((c) => c.episode_id === q.episode_id && c.version === version) : undefined;
  const chatOpen = chat?.state === 'open';
  const record = q ? [...board.gate.records].reverse().find((r) => r.episode_id === q.episode_id && r.version === version) : undefined;
  const key = q ? `${q.episode_id}:v${version}` : '';

  const run = async (p: Promise<{ board: Board | null; error: string | null }>) => {
    setBusy(true); setError(null);
    const out = await p;
    setBusy(false);
    if (out.board) onBoard(out.board);
    if (out.error) setError(out.error);
    return !out.error;
  };

  const decided = q && q.state !== 'at_gate';
  const gText = q?.state === 'passed' ? '✓ PASSED · handed to Marty (Director) · his first tab'
    : q?.state === 'kicked_back' ? (chat && chat.messages.some((m) => m.who === 'Matt') && chat.state === 'closed'
      ? `✕ KICKED BACK · chat saved · v${version} deleted when v${version + 1} is submitted`
      : '✕ KICKED BACK · open the chat and give Sawyer the direction') : '';

  return (
    <section style={sx('display: flex; flex-wrap: wrap; gap: 18px; align-items: flex-start')}>
      <div className="card" style={sx('flex: 0 1 270px; min-width: 240px; display: flex; flex-direction: column; gap: 12px')}>
        <div style={sx('display: flex; justify-content: space-between; align-items: baseline; gap: 10px')}>
          <span className="lbl">Queue</span>
          <span className="src">{waiting} waiting</span>
        </div>
        {queue.length === 0 ? <span style={sx('font-size: 13.5px; line-height: 1.5; color: rgba(220,228,230,0.75)')}>Nothing has reached Gate 1 yet.</span> : null}
        {queue.map((x) => {
          const [tag, tc] = tagOfState(x.state);
          const on = q && x.episode_id === q.episode_id;
          return (
            <button key={x.episode_id} onClick={() => { setSel(x.episode_id); setError(null); }}
              style={sx('display:flex;flex-direction:column;gap:4px;text-align:left;padding:12px 14px;border-radius:10px;cursor:pointer;font-family:inherit;color:inherit;' + (on ? 'background:rgba(60,150,230,0.16);border:1px solid #3c96e6' : 'background:rgba(255,255,255,0.025);border:1px solid rgba(255,255,255,0.06)'))}>
              <span style={sx('display: flex; justify-content: space-between; gap: 8px; width: 100%')}>
                <span style={sx("font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600; color: #f4f6f7")}>{x.episode_id}</span>
                <span style={{ ...sx("font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.1em"), color: tc }}>{tag}</span>
              </span>
              <span style={sx('font-size: 14px; color: #e8eef0')}>"{x.title || ''}"</span>
              <span className="src">{[`v${x.version ?? '?'}`, runtime(x.runtime_s), x.state === 'at_gate' ? `waiting ${since(x.dropped_at)}` : ''].filter(Boolean).join(' · ')}</span>
            </button>
          );
        })}
        <span className="src">from Sawyer's loop · oldest first</span>
      </div>

      <div className="card" style={sx('flex: 3 1 560px; min-width: 0; display: flex; flex-direction: column; gap: 16px')}>
        <div style={sx('display: flex; justify-content: space-between; align-items: baseline; gap: 10px; flex-wrap: wrap')}>
          <span className="lbl">Gate 1 · Writer → Director</span>
          <span className="src">{q && q.state === 'at_gate' ? `waiting ${since(q.dropped_at)}` : ''}</span>
        </div>
        {q ? (
          <>
            <span style={sx('font-size: 18px; font-weight: 600; color: #f4f6f7')}>{`${q.episode_id} · "${q.title || ''}" · v${version}${q.runtime_s !== undefined ? ' · ' + runtime(q.runtime_s) : ''}`}</span>
            <div style={sx('display: flex; flex-direction: column; gap: 8px')}>
              <span className="src">Sawyer's own checks</span>
              {(script?.self_checks || []).map((k, i) => (
                <div key={i} style={sx('display: flex; gap: 10px; font-size: 13.5px; color: rgba(232,238,240,0.88)')}>
                  <span style={{ ...sx("font-family: 'JetBrains Mono', monospace"), color: k.pass ? G : R }}>{k.pass ? '✓' : '✕'}</span>
                  <span>{k.check}{!k.pass && k.why ? ` — ${k.why}` : ''}</span>
                </div>
              ))}
              {script && (script.self_checks || []).length === 0 ? <span className="src">no self-checks on this version</span> : null}
            </div>
            {script ? <DocView doc={docLines(script.document)} /> : <span className="src">the script for this version is not on record</span>}
            {!decided ? (
              <>
                <label style={sx('display: flex; flex-direction: column; gap: 6px')}>
                  <span className="src">Note for Marty · sent with a pass</span>
                  <textarea rows={3} placeholder="watch for this, build around this…" value={notes[key] || ''}
                    onChange={(e) => setNotes({ ...notes, [key]: e.target.value })}
                    style={sx("font-family: 'Inter', sans-serif; font-size: 13.5px; line-height: 1.5; padding: 10px 12px; border-radius: 9px; border: 1px solid rgba(95,184,240,0.25); background: rgba(4,6,14,0.7); color: #e8eef0; resize: vertical")} />
                </label>
                <div style={sx('display: flex; flex-wrap: wrap; gap: 8px')}>
                  <button className="btn btn-pass" disabled={busy} onClick={() => run(gateDecide(q.episode_id, version, 'pass', notes[key] || ''))}>✓ Pass to Marty</button>
                  <button className="btn btn-kick" disabled={busy} onClick={() => run(gateDecide(q.episode_id, version, 'kick_back', ''))}>✕ Kick back</button>
                </div>
              </>
            ) : (
              <div style={sx('display: flex; flex-direction: column; gap: 6px')}>
                <span style={{ ...sx("font-family: 'JetBrains Mono', monospace; font-size: 12.5px"), color: q.state === 'passed' ? G : R }}>{gText}</span>
                {q.state === 'passed' && record && record.note_for_marty ? <span className="src">note for Marty: "{record.note_for_marty}"</span> : null}
              </div>
            )}
          </>
        ) : <span style={sx('font-size: 13.5px; line-height: 1.5; color: rgba(220,228,230,0.75)')}>When Sawyer drops a script at the gate, it is here for you to read, pass or kick back.</span>}
        {error ? <span className="src" style={{ color: R }}>{error}</span> : null}
        <span className="src">Gate 1 · the same gate shows on Overview and Home</span>
      </div>

      <div className="card" style={sx('flex: 1 1 320px; min-width: 0; display: flex; flex-direction: column; gap: 12px')}>
        <span className="lbl">Chat with Sawyer about this script</span>
        <span className="src">opening this chat pauses Sawyer's loop · saved to his T2 · separate from his personality</span>
        {q && chatOpen ? (
          <>
            <div style={sx('display: flex; flex-direction: column; gap: 10px')}>
              {chat!.messages.map((m, i) => (
                <div key={i} style={bubble(m.who === 'Matt', '88%')}>
                  <span className="src">{m.who}</span>
                  <span style={sx('font-size: 14px; line-height: 1.55; color: #e8eef0')}>{m.text}</span>
                </div>
              ))}
            </div>
            <div style={sx('display: flex; gap: 8px; flex-wrap: wrap; align-items: flex-end')}>
              <label style={sx('flex: 1 1 180px; display: flex; flex-direction: column; gap: 6px')}>
                <span className="src">Message Sawyer</span>
                <input value={draft} onChange={(e) => setDraft(e.target.value)}
                  style={sx("font-family: 'Inter', sans-serif; font-size: 14px; min-height: 44px; padding: 10px 12px; border-radius: 10px; border: 1px solid rgba(95,184,240,0.3); background: rgba(4,6,14,0.75); color: #e8eef0")} />
              </label>
              <button className="btn" disabled={busy || !draft.trim()} style={sx('min-height: 44px')}
                onClick={async () => { const t = draft.trim(); if (!t) return; if (await run(gateChat('send', q.episode_id, version, t))) setDraft(''); }}>{busy ? 'Sawyer is answering…' : 'Send'}</button>
            </div>
            <button className="btn" disabled={busy} style={sx('align-self: flex-start')} onClick={() => run(gateChat('close', q.episode_id, version))}>Close chat · resume loop</button>
          </>
        ) : q ? (
          <>
            <span style={sx('font-size: 13.5px; line-height: 1.5; color: rgba(220,228,230,0.75)')}>
              {`${chat ? chat.messages.length : 0} ${chat && chat.messages.length === 1 ? 'message' : 'messages'} saved on ${q.episode_id} · opening it pauses the loop`}
            </span>
            <button className="btn" disabled={busy} style={sx('align-self: flex-start')} onClick={() => run(gateChat('open', q.episode_id, version))}>Open chat</button>
          </>
        ) : <span className="src">no script selected</span>}
        {chatOpen ? <span style={{ ...sx("font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.1em"), color: A }}>LOOP PAUSED</span> : null}
      </div>
    </section>
  );
}
